import { useEffect, useState, type FormEvent } from "react";
import { settingsService } from "@/services";
import type { CommissionType, DividendTiming, GroupDefaults } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { canEditBusinessSettings } from "@/config/roles";
import { COMMISSION_TYPES, DIVIDEND_TIMING_OPTIONS, DAY_OPTIONS, DEFAULT_RESERVED_CYCLE } from "@/config/groups";
import { PageHeader } from "@/components/feature/PageHeader";
import { SettingsNav } from "../components/SettingsNav";
import { Card } from "@/components/base/Card";
import { Field } from "@/components/base/Field";
import { TextField } from "@/components/base/TextField";
import { Select } from "@/components/base/Select";
import { Toggle } from "@/components/base/Toggle";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";

const RESERVED_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `Cycle ${i + 1}` }));

export default function DefaultsSettingsPage() {
  const { role, user } = useAuth();
  const toast = useToast();
  const canEdit = canEditBusinessSettings(role);

  const [defaults, setDefaults] = useState<GroupDefaults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    settingsService
      .getGroupDefaults()
      .then((d) => active && setDefaults(d))
      .catch((e) => active && setError(e instanceof Error ? e.message : "Could not load defaults."));
    return () => {
      active = false;
    };
  }, []);

  const set = (field: keyof GroupDefaults, value: unknown) =>
    setDefaults((d) => (d ? { ...d, [field]: value } : d));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!defaults || saving || !canEdit) return;
    setSaving(true);
    try {
      const saved = await settingsService.updateGroupDefaults(defaults, user?.name ?? "Staff");
      setDefaults(saved);
      toast.success("Defaults saved", "New groups will start from these values.");
    } catch (err) {
      toast.error("Could not save", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Organization configuration, staff users and the audit trail." />
      <SettingsNav />

      {error ? (
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      ) : !defaults ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <form onSubmit={save} className="space-y-4">
          {!canEdit ? (
            <Alert variant="info" title="Read-only">Only Admin and Super Admin can edit group defaults.</Alert>
          ) : (
            <Alert variant="info" title="Starting values only">
              These pre-fill the new-group wizard. They are not locked — each group's own configuration always wins.
            </Alert>
          )}

          <Card title="Policies" subtitle="Pre-filled when a group is created in the wizard.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Reserved organizer cycle">
                <Select
                  value={String(defaults.reservedCycle)}
                  onChange={(v) => set("reservedCycle", Number(v))}
                  disabled={!canEdit}
                  options={RESERVED_OPTIONS}
                />
              </Field>
              <Field label="Commission type">
                <Select
                  value={defaults.commissionType}
                  onChange={(v) => set("commissionType", v as CommissionType)}
                  disabled={!canEdit}
                  options={COMMISSION_TYPES.map((c) => ({ value: c.value, label: c.label }))}
                />
              </Field>
              <Field label="Commission value" helper="Ignored when commission is None.">
                <input
                  type="number"
                  min={0}
                  step={defaults.commissionType === "fixed" ? "any" : "0.01"}
                  value={defaults.commissionValue}
                  onChange={(e) => set("commissionValue", Number(e.target.value) || 0)}
                  disabled={!canEdit || defaults.commissionType === "none"}
                  className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:bg-background-100 disabled:text-foreground-500"
                />
              </Field>
              <TextField
                label="Grace period (days)"
                inputMode="numeric"
                value={String(defaults.gracePeriodDays)}
                onChange={(e) => set("gracePeriodDays", Number(e.target.value) || 0)}
                disabled={!canEdit}
              />
              <Field label="Late fee">
                <input
                  type="number"
                  min={0}
                  value={defaults.lateFee}
                  onChange={(e) => set("lateFee", Number(e.target.value) || 0)}
                  disabled={!canEdit}
                  className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:bg-background-100 disabled:text-foreground-500"
                />
              </Field>
              <div className="flex items-end gap-4 pb-1">
                <Toggle checked={defaults.lateFeeIsPercent} onChange={(v) => set("lateFeeIsPercent", v)} disabled={!canEdit} size="sm" label="Percentage" />
                <Toggle checked={defaults.lateFeePerDay} onChange={(v) => set("lateFeePerDay", v)} disabled={!canEdit} size="sm" label="Per day" />
              </div>
            </div>
          </Card>

          <Card title="Auction & billing">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField label="Bid step (₹)" inputMode="numeric" value={String(defaults.bidStep)} onChange={(e) => set("bidStep", Number(e.target.value) || 0)} disabled={!canEdit} />
              <div className="flex items-end">
                <Toggle checked={defaults.blockOverdueBidding} onChange={(v) => set("blockOverdueBidding", v)} disabled={!canEdit} size="sm" label="Block overdue bidders" />
              </div>
              <Field label="Dividend timing">
                <Select
                  value={defaults.dividendTiming}
                  onChange={(v) => set("dividendTiming", v as DividendTiming)}
                  disabled={!canEdit}
                  options={DIVIDEND_TIMING_OPTIONS.map((d) => ({ value: d.value, label: d.label }))}
                />
              </Field>
              <div className="sm:col-span-1" />
              <Field label="Auction day (of month)">
                <Select value={String(defaults.auctionDay)} onChange={(v) => set("auctionDay", Number(v))} disabled={!canEdit} options={DAY_OPTIONS} />
              </Field>
              <Field label="Installment due day (of month)">
                <Select value={String(defaults.dueDay)} onChange={(v) => set("dueDay", Number(v))} disabled={!canEdit} options={DAY_OPTIONS} />
              </Field>
            </div>
          </Card>

          {canEdit ? (
            <div className="flex justify-end">
              <Button type="submit" loading={saving} icon={<i className="ri-save-line" />}>
                Save defaults
              </Button>
            </div>
          ) : null}
          <p className="text-xs text-foreground-400">Default reserved cycle: {DEFAULT_RESERVED_CYCLE}</p>
        </form>
      )}
    </div>
  );
}